import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import actions from '@store/actions/tourbookPage';
import { useTranslation } from 'react-i18next';
import useEnv from '@shared/useEnv';
import {
  addListener,
  addListenerOnce,
  loadMap,
  createMap,
  getMapOptions,
  latLngBounds,
} from '@shared/googleMaps';
import Markers from '@shared/map/MarkersDeprecated';
import HoveredListingIndicator from '@shared/map/HoveredListingIndicator';
import { CustomIcon } from '@components/shared';
import { TourbookListing } from '@root/types';
import debounce from 'lodash/debounce';
import classnames from 'classnames';
import s from '../Tourbook.module.less';

type Props = {
  tourbook: any;
  hoveredListing: TourbookListing | null;
  className?: any;
  maxHeight?: number;
};

const TourbookMap = ({ tourbook, hoveredListing, className = {}, maxHeight }: Props) => {
  const { t } = useTranslation('tourbook');
  const { googleMapsKey: key } = useEnv();
  const dispatch = useDispatch();
  const [useAutofocus, setAutofocus] = useState(false);
  const [height, setHeight] = useState({});
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef() as { current: google.maps.Map | null };
  const markers = useRef() as { current: Markers | null };
  const hoveredListingIndicator = useRef() as { current: HoveredListingIndicator };

  useEffect(() => {
    setHeight({});
    if (maxHeight && maxHeight < (mapRef.current?.clientHeight || 0))
      setHeight({ height: `${maxHeight}px` });
  }, [mapRef, maxHeight]);

  const zoomToPoints = () => {
    const bounds = latLngBounds() as google.maps.LatLngBounds;
    markers.current?.getMarkers().forEach(marker => {
      const position = marker.getPosition();
      if (position) bounds?.extend(position);
    });
    mapObj.current?.fitBounds(bounds);
  };

  const reportZoom = debounce(() => {
    const zoom = mapObj.current?.getZoom();
    dispatch(actions.mapZoom(zoom));
  }, 500);

  const updateMarkers = (reZoom = true) => {
    if (!tourbook) return;

    const listings = tourbook.listings.filter(listing => !listing.flaggedAt);

    if (listings) {
      markers.current?.createMarkers(listings, mapObj.current, []);
      if (!reZoom) return;
      zoomToPoints();
    }
  };

  const handleHover = () => {
    if (!mapObj.current) return;

    if (hoveredListing) {
      hoveredListingIndicator.current.addHoveredListingMarker(hoveredListing, mapObj.current);
    } else {
      hoveredListingIndicator.current.clearHoveredListingMarker();
    }
  };

  useEffect(() => {
    loadMap(key!, () => {
      if (mapObj.current) {
        updateMarkers();
      } else {
        if (!mapRef.current) return;
        // create map
        const startingPoint = { long: -73.99158, lat: 40.740038 };

        mapObj.current = createMap(
          mapRef.current,
          getMapOptions({
            clickableIcons: false,
            draggableCursor: 'default',
            draggingCursor: 'default',
            zoom: 13,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
            scaleControl: true,
            zoomControl: true,
            ...startingPoint,
            gestureHandling: 'auto',
          }),
        );

        // dom dependent objects
        markers.current = new Markers(false, null, null, false);

        // a "loaded" event doesn't exist
        // handling the first idle event emulates it
        addListenerOnce(mapObj.current, 'idle', () => {
          addListener(mapObj.current, 'zoom_changed', reportZoom);
        });
        hoveredListingIndicator.current = new HoveredListingIndicator({
          mapMarkerText: t('common:map.markerText'),
          disableAutoPan: true,
          enableZoomToPoint: useAutofocus,
        });

        updateMarkers();
      }
    });
  }, [tourbook]);

  useEffect(() => {
    if (!mapObj?.current?.data) return;

    hoveredListingIndicator.current.clearHoveredListingMarker();
    hoveredListingIndicator.current = new HoveredListingIndicator({
      mapMarkerText: t('common:map.markerText'),
      disableAutoPan: true,
      enableZoomToPoint: useAutofocus,
    });
    mapObj.current.setOptions({ gestureHandling: useAutofocus ? 'none' : 'auto' });

    updateMarkers(false);
    handleHover();
  }, [useAutofocus]);

  useEffect(handleHover, [hoveredListing]);

  const toggleAutofocus = () => {
    setAutofocus(!useAutofocus);
    dispatch(actions.mapAutofocusToggled(useAutofocus));
  };

  const resetAutofocus = () => {
    setAutofocus(false);
    zoomToPoints();
  };

  return (
    <div className={classnames(className, s.noBrowserFocusVisible)} style={height}>
      <>
        <div className={s.autofocusContainer} role="button" onClick={toggleAutofocus}>
          <div className={useAutofocus ? s.enabled : undefined}>
            <CustomIcon type="union" title={t('autofocus.map')} />
          </div>
          <div className={useAutofocus ? s.enabled : undefined}>
            {t(`autofocus.${useAutofocus ? 'enabled' : 'disabled'}`)}
          </div>
        </div>
        <div className={s.resetFocusContainer} role="button" onClick={resetAutofocus}>
          <div>
            <CustomIcon type="map-refresh" title={t('autofocus.reset.tooltip')} />
          </div>
          <div>{t('autofocus.reset.text')}</div>
        </div>
      </>
      <div className={s.map} data-testid="tourbook-map" ref={mapRef}></div>
    </div>
  );
};

export default TourbookMap;
